/**
 * 사용자 프로필 API
 * @see BE #200 - 사용자 프로필 API 구현
 */

import { apiClient } from './client';
import { recoverSafeRead } from './recoverSafeRead';
import { READ_REQUEST_ID_HEADER } from '@/utils/transportDiagnostics';
import type {
  UserProfileResponse,
  UpdateProfileRequest,
  UserPreferenceResponse,
  UpdatePreferenceRequest,
  CompanyListResponse,
  CompanyResponse,
  CreateCompanyRequest,
  UpdateCompanyRequest,
} from './types';

/**
 * 사용자 프로필 API
 */
export const userApi = {
  /**
   * 현재 사용자 프로필 조회
   * GET /api/v1/users/me
   */
  getProfile: async (signal?: AbortSignal): Promise<UserProfileResponse> => {
    return recoverSafeRead('profile-read', signal, (timeout, requestId) =>
      apiClient.get<UserProfileResponse>('/api/v1/users/me', {
        signal,
        timeout,
        ...(requestId ? { headers: { [READ_REQUEST_ID_HEADER]: requestId } } : {}),
      }),
    );
  },

  /**
   * 사용자 프로필 수정
   * PUT /api/v1/users/me
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<UserProfileResponse> => {
    return apiClient.put<UserProfileResponse>('/api/v1/users/me', data);
  },

  /**
   * 환경설정 조회
   * GET /api/v1/users/me/preference
   */
  getPreference: async (): Promise<UserPreferenceResponse> => {
    return apiClient.get<UserPreferenceResponse>('/api/v1/users/me/preference');
  },

  /**
   * 환경설정 수정
   * PUT /api/v1/users/me/preference
   */
  updatePreference: async (data: UpdatePreferenceRequest): Promise<UserPreferenceResponse> => {
    return apiClient.put<UserPreferenceResponse>('/api/v1/users/me/preference', data);
  },

  /**
   * 회사 목록 조회
   * GET /api/v1/users/me/companies
   */
  getCompanies: async (): Promise<CompanyListResponse> => {
    return apiClient.get<CompanyListResponse>('/api/v1/users/me/companies');
  },

  /**
   * 회사 상세 조회
   * GET /api/v1/users/me/companies/{companyId}
   */
  getCompany: async (companyId: number): Promise<CompanyResponse> => {
    return apiClient.get<CompanyResponse>(`/api/v1/users/me/companies/${companyId}`);
  },

  /**
   * 회사 생성
   * POST /api/v1/users/me/companies
   */
  createCompany: async (data: CreateCompanyRequest): Promise<CompanyResponse> => {
    return apiClient.post<CompanyResponse>('/api/v1/users/me/companies', data);
  },

  /**
   * 회사 수정
   * PUT /api/v1/users/me/companies/{companyId}
   */
  updateCompany: async (companyId: number, data: UpdateCompanyRequest): Promise<CompanyResponse> => {
    return apiClient.put<CompanyResponse>(`/api/v1/users/me/companies/${companyId}`, data);
  },

  /**
   * 회사 삭제
   * DELETE /api/v1/users/me/companies/{companyId}
   */
  deleteCompany: async (companyId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete(`/api/v1/users/me/companies/${companyId}`);
  },

  /**
   * 기본 회사 설정
   * POST /api/v1/users/me/companies/{companyId}/default
   */
  setDefaultCompany: async (companyId: number): Promise<CompanyResponse> => {
    return apiClient.post<CompanyResponse>(`/api/v1/users/me/companies/${companyId}/default`);
  },
};
